import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import axiosInstance from "../../../../Services/axiosCommon";
import { useToast } from "../../../../Components/Others/ToastServices";
import { NuInput } from "../../../../Components/Utils/Input";
import FormikSelectTwo from "../../../../Components/Utils/Select";
import UseDropDown from "../../../../Components/Hooks/useDropDown";
import {
  CreateButton,
  CancelButton,
  SubmitButton,
} from "../../../../Components/Utils/Buttons";
import Breadcrumb from "../../../../Components/Utils/Breadcrumb";
import ModalContainer from "../../../../Components/Others/ModalContainer";
import Pagination from "../../../../Components/Utils/Pagenation";

function EmailParserSettings() {
  const toast = useToast();
  const { data: userList } = UseDropDown("/user/drop-down");

  const [accounts, setAccounts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pageIndex, setPageIndex] = useState(1);
  const [dataCount, setDataCount] = useState(
    Number(localStorage.getItem("perPageView")) || 20
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Fetch Accounts
  const fetchAccounts = () => {
    setLoading(true);
    axiosInstance
      .get("/email_parser/accounts", {
        params: {
          page: pageIndex,
          per_page: dataCount,
        },
      })
      .then((res) => {
        const result = res?.data;
        if (result?.data && Array.isArray(result.data)) {
          setAccounts(result.data);
          setTotalCount(result.total || result.data.length);
        } else if (Array.isArray(result)) {
          setAccounts(result);
          setTotalCount(result.length);
        } else {
          setAccounts([]);
          setTotalCount(0);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch email parser accounts:", err);
        toast.open({
          type: "error",
          message: "Error",
          description: "Failed to fetch email parser accounts.",
        });
        setAccounts([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchAccounts();
  }, [pageIndex, dataCount]);

  // Formik Configuration
  const initialValues = {
    receiver_email: selectedAccount?.receiver_email || "",
    password: "",
    user_id: selectedAccount?.user_id || "",
    imap_server: selectedAccount?.imap_server || "",
    is_active: selectedAccount?.is_active ?? true,
  };

  const validationSchema = yup.object({
    receiver_email: yup
      .string()
      .email("Must be a valid email address")
      .required("Receiver email is required"),
    imap_server: yup.string().required("IMAP server is required"),
    user_id: yup.string().required("Assigned user is required"),
    password: isEditMode
      ? yup.string()
      : yup.string().required("Password is required"),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      const payload = {
        receiver_email: values.receiver_email,
        imap_server: values.imap_server,
        user_id: Number(values.user_id),
        is_active: Boolean(values.is_active),
      };

      if (values.password) {
        payload.password = values.password;
      }

      if (isEditMode && selectedAccount) {
        const accountId = selectedAccount.id || selectedAccount.account_id;
        axiosInstance
          .put(`/email_parser/accounts/${accountId}`, payload)
          .then((res) => {
            if (res.data?.status !== false) {
              toast.open({
                type: "success",
                message: "Success",
                description:
                  res.data?.msg || "Email parser account updated successfully.",
              });
              closeModal();
              fetchAccounts();
            } else {
              toast.open({
                type: "error",
                message: "Error",
                description: res.data?.error || "Failed to update account.",
              });
            }
          })
          .catch((err) => {
            toast.open({
              type: "error",
              message: "Error",
              description:
                err.response?.data?.error || "Failed to update account.",
            });
          });
      } else {
        axiosInstance
          .post("/email_parser/accounts", payload)
          .then((res) => {
            if (res.data?.status !== false) {
              toast.open({
                type: "success",
                message: "Success",
                description:
                  res.data?.msg || "Email parser account created successfully.",
              });
              closeModal();
              fetchAccounts();
            } else {
              toast.open({
                type: "error",
                message: "Error",
                description: res.data?.error || "Failed to create account.",
              });
            }
          })
          .catch((err) => {
            toast.open({
              type: "error",
              message: "Error",
              description:
                err.response?.data?.error || "Failed to create account.",
            });
          });
      }
    },
  });

  const openCreateModal = () => {
    setSelectedAccount(null);
    setIsEditMode(false);
    formik.resetForm();
    setIsModalOpen(true);
  };

  const openEditModal = (account) => {
    setSelectedAccount(account);
    setIsEditMode(true);
    formik.resetForm();
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedAccount(null);
    setIsEditMode(false);
    formik.resetForm();
  };

  // Toggle Active Status
  const handleToggleActive = (account) => {
    const accountId = account.id || account.account_id;
    const newStatus = !account.is_active;

    axiosInstance
      .put(`/email_parser/accounts/${accountId}`, {
        is_active: newStatus,
      })
      .then((res) => {
        if (res.data?.status !== false) {
          toast.open({
            type: "success",
            message: "Status Updated",
            description: `Account is now ${newStatus ? "Active" : "Inactive"}.`,
          });
          setAccounts((prev) =>
            prev.map((acc) =>
              (acc.id || acc.account_id) === accountId
                ? { ...acc, is_active: newStatus }
                : acc
            )
          );
        } else {
          toast.open({
            type: "error",
            message: "Error",
            description: res.data?.error || "Failed to update status.",
          });
        }
      })
      .catch(() => {
        toast.open({
          type: "error",
          message: "Error",
          description: "Failed to update status.",
        });
      });
  };

  // Delete Handler
  const openDeleteModal = (account) => {
    setSelectedAccount(account);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = () => {
    if (!selectedAccount) return;
    const accountId = selectedAccount.id || selectedAccount.account_id;

    axiosInstance
      .delete(`/email_parser/accounts/${accountId}`)
      .then((res) => {
        if (res.data?.status !== false) {
          toast.open({
            type: "success",
            message: "Success",
            description:
              res.data?.msg || "Email parser account deleted successfully.",
          });
          setIsDeleteModalOpen(false);
          setSelectedAccount(null);
          fetchAccounts();
        } else {
          toast.open({
            type: "error",
            message: "Error",
            description: res.data?.error || "Failed to delete account.",
          });
        }
      })
      .catch((err) => {
        toast.open({
          type: "error",
          message: "Error",
          description:
            err.response?.data?.error || "Failed to delete account.",
        });
      });
  };

  // Helper to get User Name from userList
  const getUserName = (userId) => {
    if (!userId) return "N/A";
    const foundUser = userList?.find(
      (u) => String(u.value) === String(userId)
    );
    return foundUser ? foundUser.label : `User #${userId}`;
  };

  return (
    <div className="w-full h-full p-2 lg:px-4">
      {/* Top Breadcrumb & Header */}
      <div className="w-full flex flex-row justify-between items-center gap-3 mb-4">
        <div>
          <Breadcrumb
            items={[
              { name: "Settings", link: "/settings" },
              { name: "Email Parser" },
            ]}
          />
          <p className="text-xs text-gray-500 mt-1 hidden sm:block">
            Configure email inboxes to automatically monitor and parse PRs.
          </p>
        </div>

        <CreateButton
          label="Add Parser Account"
          handleClick={openCreateModal}
        />
      </div>

      {/* Info Card */}
      {/* <div className="w-full bg-emerald-50/60 border border-emerald-200/80 rounded-xl p-2 flex items-center mb-2 gap-3">
        <div className="w-8 h-8 rounded-lg bg-emerald-500 text-white flex items-center justify-center shrink-0 shadow-sm">
          <svg
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </div>
        <div className="text-xs text-emerald-900">
          <span className="font-bold">Automated Email Parser:</span> Monitored
          inboxes will automatically ingest incoming emails, extract PR
          details, and assign generated PRs to the configured system user.
        </div>
      </div> */}

      {/* Main Table Content */}
      <div className="w-full bg-white rounded-xl shadow-sm border border-gray-100 p-3 ">
        <div className="overflow-x-auto h-[calc(100vh-215px)]">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-gray-200/70 text-[11px] font-bold text-gray-500 uppercase tracking-wider bg-gray-50/50">
                {/* <th className="p-3">ID</th> */}
                <th className="p-3">Receiver Email</th>
                <th className="p-3">IMAP Server</th>
                <th className="p-3">Assigned YPR Creator</th>
                <th className="p-3 text-center">Status</th>
                <th className="p-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-xs min-h-fit">
              {loading ? (
                <tr>
                  <td colSpan="6" className="p-6 text-center text-gray-400">
                    Loading email parser accounts...
                  </td>
                </tr>
              ) : accounts.length > 0 ? (
                accounts.map((account, idx) => {
                  const accountId = account.id || account.account_id || idx + 1;
                  const isActive = Boolean(account.is_active);

                  return (
                    <tr
                      key={accountId}
                      className="hover:bg-gray-50/70 transition-colors items-start h-10"
                    >
                      {/* ID */}
                      {/* <td className="p-3 font-semibold text-gray-600 h-fit">
                        #{accountId}
                      </td> */}

                      {/* Receiver Email */}
                      <td className="p-3 font-bold text-gray-800 h-fit">
                        <div className="flex items-center gap-2">
                          <svg
                            className="w-4 h-4 text-emerald-600 shrink-0"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                            />
                          </svg>
                          <span>{account.receiver_email}</span>
                        </div>
                      </td>

                      {/* IMAP Server */}
                      <td className="p-3 text-gray-600 font-medium h-fit">
                        <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded text-[11px] font-mono">
                          {account.imap_server || "N/A"}
                        </span>
                      </td>

                      {/* Assigned Creator */}
                      <td className="p-3 text-gray-700 font-medium h-fit">
                        {getUserName(account.user_id)}
                      </td>

                      {/* Status */}
                      <td className="p-3 text-center h-fit">
                        <button
                          onClick={() => handleToggleActive(account)}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold transition-all cursor-pointer ${isActive
                            ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                            : "bg-rose-100 text-rose-800 hover:bg-rose-200"
                            }`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-emerald-600" : "bg-rose-600"
                              }`}
                          />
                          {isActive ? "Active" : "Inactive"}
                        </button>
                      </td>

                      {/* Actions */}
                      <td className="p-3 text-center h-fit">
                        <div className="flex items-center justify-center gap-2">
                          {/* Edit */}
                          <button
                            onClick={() => openEditModal(account)}
                            className="p-1.5 rounded-md border border-sky-200 text-sky-600 hover:bg-sky-50 transition-colors"
                            title="Edit Account"
                          >
                            <svg
                              className="w-3.5 h-3.5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M11 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-5M18.5 2.5a2.121 2.121 0 113 3L12 15l-4 1 1-4 9.5-9.5z"
                              />
                            </svg>
                          </button>

                          {/* Delete */}
                          <button
                            onClick={() => openDeleteModal(account)}
                            className="p-1.5 rounded-md border border-rose-200 text-rose-500 hover:bg-rose-50 transition-colors"
                            title="Delete Account"
                          >
                            <svg
                              className="w-3.5 h-3.5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" className="p-8 text-center text-gray-400">
                    No email parser accounts configured yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalCount > 0 && (
          <div className=" bottom-0 w-full mt-3 border-t border-gray-100 pt-2">
            <Pagination
              pageIndex={pageIndex}
              setPageIndex={setPageIndex}
              dataCount={dataCount}
              setDataCount={setDataCount}
              total={totalCount}
            />
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 animate-scale-in border border-gray-100">
            <div className="flex justify-between items-center pb-3 mb-4 border-b border-gray-100">
              <h3 className="text-base font-bold text-gray-800">
                {isEditMode
                  ? "Edit Email Parser Account"
                  : "Add New Email Parser Account"}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <form onSubmit={formik.handleSubmit} className="flex flex-col gap-4">
              {/* Receiver Email */}
              <NuInput
                type="email"
                label="Receiver Email"
                placeholder="e.g. parser@numota.in"
                formik={formik}
                name="receiver_email"
                isRequired={true}
                width="w-full"
              />

              {/* Password */}
              <NuInput
                type="password"
                label={
                  isEditMode ? "Password (Leave blank to keep unchanged)" : "Password"
                }
                placeholder="••••••••"
                formik={formik}
                name="password"
                isRequired={!isEditMode}
                width="w-full"
              />

              {/* IMAP Server */}
              <NuInput
                type="text"
                label="IMAP Server"
                placeholder="e.g. mail.numota.in"
                formik={formik}
                name="imap_server"
                isRequired={true}
                width="w-full"
              />

              {/* Assigned Creator (User ID) */}
              <FormikSelectTwo
                label="Assigned YPR Creator (User)"
                placeholder="Choose User"
                Options={userList}
                formik={formik}
                name="user_id"
                isRequired={true}
                width="w-full"
                FixPosition={false}
                PortalTarget={false}
              />

              {/* Is Active Toggle */}
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="checkbox"
                  id="is_active"
                  name="is_active"
                  checked={formik.values.is_active}
                  onChange={(e) =>
                    formik.setFieldValue("is_active", e.target.checked)
                  }
                  className="w-4 h-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500 cursor-pointer"
                />
                <label
                  htmlFor="is_active"
                  className="text-xs font-semibold text-gray-700 cursor-pointer select-none"
                >
                  Enable monitoring (Active)
                </label>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-2 pt-4 border-t border-gray-100 mt-2">
                <CancelButton handleClick={closeModal} />
                <SubmitButton name={isEditMode ? "Update" : "Create"} />
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ModalContainer
        visiable={isDeleteModalOpen}
        title="Delete Email Parser Account"
        content={`Are you sure you want to remove the email parser account "${selectedAccount?.receiver_email}"?`}
        handleClick={handleDelete}
        closeModal={() => setIsDeleteModalOpen(false)}
      />
    </div>
  );
}

export default EmailParserSettings;
